import React, { FC, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { WarehouseMapConstructor, getSelectedFloorId, warehouseMapConstructorActions } from 'features/WarehouseMapConstructor';
import { ZonesList } from 'widgets/ZonesList';
import { useParams, useSearchParams } from 'react-router-dom';
import { getFeatureFlags } from 'app/appState';
import { sortFloorsByOrder } from 'entities/Floor';
import { useGetWarehouseByIdQuery } from 'entities/Warehouse';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { useAppDispatch, useAppSelector } from 'app/config/storeConfig/hooks';
import { ReactComponent as BoxIcon } from 'shared/assets/icons/BoxTabIcon.svg';
import { ReactComponent as SizeCodeIcon } from 'shared/assets/icons/SizeCodeTabIcon.svg';
import { ReactComponent as WarehouseIcon } from 'shared/assets/icons/WarehouseIcon.svg';
import { TabSwitcher } from 'shared/ui/TabSwitcher';

const VALID_TAB_KEYS = ['Boxes', 'Map', 'Size codes', 'Zones', 'Locks controller'] as const;

const resolveFloorFromUrl = (params: URLSearchParams): Nullable<string> => params.get('floorId') ?? params.get('floor');

export const WarehouseConstructorPage: FC = memo(() => {
  const { warehouseId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const { t } = useAppTranslation('warehouse-management');

  const dispatch = useAppDispatch();
  const featureFlags = useAppSelector(getFeatureFlags);
  const isMapConstructorEnabled = featureFlags?.mapConstructor !== false;

  const [selectedTabKey, setSelectedTabKey] = useState<string>(() => {
    const urlTab = searchParams.get('tab') ?? '';
    return (VALID_TAB_KEYS as readonly string[]).includes(urlTab) ? urlTab : 'Map';
  });

  const { data, refetch } = useGetWarehouseByIdQuery({ warehouseId, params: { floors: true, internalEquipment: true } });

  const selectedFloorId = useAppSelector(getSelectedFloorId);

  const sortedFloors = useMemo(() => (data?.floors ? sortFloorsByOrder(data.floors) : undefined), [data?.floors]);

  const switchTab = useCallback(
    (tabKey: string): void => {
      setSelectedTabKey(tabKey);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('tab', tabKey);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  useEffect(() => {
    if (!sortedFloors?.length) return;

    if (selectedFloorId && sortedFloors.some((floor) => floor.floorId === selectedFloorId)) {
      return;
    }

    const urlFloorId = resolveFloorFromUrl(searchParams);
    const validFloor = urlFloorId && sortedFloors.some((floor) => floor.floorId === urlFloorId) ? urlFloorId : sortedFloors[0].floorId;

    if (validFloor !== selectedFloorId) {
      dispatch(warehouseMapConstructorActions.changeFloor(validFloor));
    }
  }, [dispatch, searchParams, selectedFloorId, sortedFloors]);

  // Keep URL in sync when the floor changes (e.g. via the FloorSwitch inside the constructor).
  useEffect(() => {
    if (!selectedFloorId) return;

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (next.get('floorId') === selectedFloorId) return prev;
        next.set('floorId', selectedFloorId);
        next.delete('floor');
        return next;
      },
      { replace: true },
    );
  }, [selectedFloorId, setSearchParams]);

  const tabs = useMemo(
    () => [
      { key: 'Boxes', label: t('Boxes'), icon: <BoxIcon /> },
      { key: 'Map', label: t('Map'), icon: <BoxIcon /> },
      { key: 'Size codes', label: t('Size codes'), icon: <SizeCodeIcon /> },
      { key: 'Zones', label: t('Zones'), icon: <WarehouseIcon /> },
      { key: 'Locks controller', label: t('Locks controller'), icon: <SizeCodeIcon /> },
    ],
    [t],
  );

  const renderTabContent = useCallback(() => {
    switch (selectedTabKey) {
      case 'Boxes':
        return null;
      case 'Map':
        return (
          <>
            {sortedFloors?.map((floor) =>
              floor.floorId === selectedFloorId ? (
                <WarehouseMapConstructor
                  key={floor.floorId}
                  floors={sortedFloors}
                  selectedFloor={floor}
                  warehouseId={warehouseId}
                  shapesConfig={floor.shapesConfig}
                  refetchFloors={refetch}
                />
              ) : null,
            )}
          </>
        );
      case 'Size codes':
        return null;
      case 'Zones':
        return <ZonesList />;
      case 'Locks controller':
        return null;
      default:
        return null;
    }
  }, [refetch, selectedFloorId, selectedTabKey, sortedFloors, warehouseId]);

  const warehouseTitle = useMemo(
    () => (
      <span key="1" className="text-2xl font-semibold">
        {data?.name}
      </span>
    ),
    [data?.name],
  );

  if (!isMapConstructorEnabled) return null;

  return (
    <div className="mt-7">
      <TabSwitcher tabs={tabs} selectedTabKey={selectedTabKey} onChangeTab={switchTab} title={warehouseTitle}>
        {renderTabContent()}
      </TabSwitcher>
    </div>
  );
});
